/**
 * Plan 07 — skeletal prototype: bone rigs as PURE data (no Babylon, unit-testable).
 *
 * The soldier/boss geometry is authored as merged parts in a deliberate stance (plan 05 P5.2/P5.4)
 * and `buildSoldier`/`buildBoss` return the per-part VERTEX RANGES. Binding is RIGID (one bone per
 * part, weight 1.0 — inside the ≤4-influence contract of the rigged-asset skill), so:
 *
 *   * the authored stance IS the deterministic bind pose (skill: "one stable rig", "deterministic
 *     bind pose") — a freshly bound mesh renders byte-identical to the pre-skeletal mesh;
 *   * a bone's bind position only chooses the PIVOT its clip rotations act about (joint positions,
 *     approximated from the authored part literals — documented, literal, no RNG).
 *
 * Sockets (skill contract) are declared as bones: soldier `root`/`hips`/`head`/`handR` (+ `weapon`
 * as the main-hand weapon socket the rifle is bound to); boss `root`/`hips` (center-mass)/`head`
 * (+ `handR` as the club socket). Bone count ≤16 keeps the standard 4-influence skinning buffers.
 */

export interface RigBone {
  name: string;
  parent: string | null;
  /** Bind position, model space (metres). Pivot for clip rotations; see file header. */
  pos: readonly [number, number, number];
  /** Socket role declared for the rigged-asset contract (informational; bones are the mechanism). */
  socket?: string;
}

export interface Rig {
  id: string;
  bones: readonly RigBone[];
}

/**
 * Soldier rig (character). Bind pivots from the authored part literals (buildSoldier): hips at the
 * pelvis, spine above the hips along the baked 0.085 rad lean, head at the helmeted head, weapon at
 * the rifle grip, legs at hip/knee/ankle of the authored mid-stride stance, arms at shoulder/elbow.
 */
export const RIG_SOLDIER: Rig = {
  id: 'soldier-v1',
  bones: [
    { name: 'root', parent: null, pos: [0, 0, 0], socket: 'root' },
    { name: 'hips', parent: 'root', pos: [0, 0.92, 0], socket: 'hips' },
    { name: 'spine', parent: 'hips', pos: [0, 1.26, 0.06] },
    { name: 'head', parent: 'spine', pos: [0, 1.58, 0.11], socket: 'head' },
    { name: 'thighL', parent: 'hips', pos: [-0.11, 0.9, 0.03] },
    { name: 'shinL', parent: 'thighL', pos: [-0.125, 0.5, 0.22] },
    { name: 'footL', parent: 'shinL', pos: [-0.115, 0.1, 0.16], socket: 'left-foot' },
    { name: 'thighR', parent: 'hips', pos: [0.11, 0.9, -0.02] },
    { name: 'shinR', parent: 'thighR', pos: [0.125, 0.5, -0.22] },
    { name: 'footR', parent: 'shinR', pos: [0.115, 0.12, -0.12], socket: 'right-foot' },
    { name: 'upperArmL', parent: 'spine', pos: [-0.215, 1.4, 0.07] },
    { name: 'foreArmL', parent: 'upperArmL', pos: [-0.2, 1.18, 0.17] },
    { name: 'upperArmR', parent: 'spine', pos: [0.215, 1.4, 0.07] },
    { name: 'foreArmR', parent: 'upperArmR', pos: [0.2, 1.18, 0.19] },
    { name: 'handR', parent: 'foreArmR', pos: [0.09, 1.23, 0.43], socket: 'right-hand' },
    { name: 'weapon', parent: 'handR', pos: [0.075, 1.27, 0.4], socket: 'main-hand-weapon' },
  ],
};

/**
 * Boss rig (monster profile — combat parts, no wardrobe). `hips` is the center-mass socket; the
 * club is rigidly bound to `handR` (the club socket), so slam/death clips articulate the whole club.
 */
export const RIG_BOSS: Rig = {
  id: 'boss-v1',
  bones: [
    { name: 'root', parent: null, pos: [0, 0, 0], socket: 'root' },
    { name: 'hips', parent: 'root', pos: [0, 1.82, 0], socket: 'center-mass' },
    { name: 'torso', parent: 'hips', pos: [0, 2.56, 0] },
    { name: 'head', parent: 'torso', pos: [0, 2.94, -0.4], socket: 'head' },
    { name: 'upperArmL', parent: 'torso', pos: [-1.02, 2.78, 0] },
    { name: 'foreArmL', parent: 'upperArmL', pos: [-1.24, 2.02, -0.2] },
    { name: 'upperArmR', parent: 'torso', pos: [1.02, 2.78, 0] },
    { name: 'foreArmR', parent: 'upperArmR', pos: [1.36, 2.38, -0.26] },
    { name: 'handR', parent: 'foreArmR', pos: [0.96, 2.62, -0.56], socket: 'club' },
    { name: 'thighL', parent: 'hips', pos: [-0.45, 1.72, 0.01] },
    { name: 'shinL', parent: 'thighL', pos: [-0.48, 1.02, 0.02] },
    { name: 'footL', parent: 'shinL', pos: [-0.52, 0.13, -0.14] },
    { name: 'thighR', parent: 'hips', pos: [0.45, 1.72, 0.03] },
    { name: 'shinR', parent: 'thighR', pos: [0.48, 1.02, 0.05] },
    { name: 'footR', parent: 'shinR', pos: [0.52, 0.13, -0.06] },
  ],
};

/** part name (from buildSoldier/buildBoss `parts[]`) → bone name. Rigid binding table. */
export const SOLDIER_PART_BONE: Record<string, string> = {
  bootL: 'footL',
  bootR: 'footR',
  shinL: 'shinL',
  shinR: 'shinR',
  thighL: 'thighL',
  thighR: 'thighR',
  pelvis: 'hips',
  torso: 'spine',
  chestPlate: 'spine',
  shoulderYoke: 'spine',
  pouchL: 'spine',
  pouchR: 'spine',
  pack: 'spine',
  head: 'head',
  helmetDome: 'head',
  helmetBrim: 'head',
  helmetBand: 'head',
  armband: 'upperArmL',
  upperArmL: 'upperArmL',
  upperArmR: 'upperArmR',
  foreArmL: 'foreArmL',
  foreArmR: 'foreArmR',
  handL: 'foreArmL',
  handR: 'handR',
  rifleStock: 'weapon',
  rifleReceiver: 'weapon',
  rifleMag: 'weapon',
  rifleBarrel: 'weapon',
};

export const BOSS_PART_BONE: Record<string, string> = {
  bootL: 'footL',
  bootR: 'footR',
  'shin-l': 'shinL',
  'shin-r': 'shinR',
  kneeL: 'shinL',
  kneeR: 'shinR',
  'thigh-l': 'thighL',
  'thigh-r': 'thighR',
  pelvis: 'hips',
  belt: 'hips',
  buckle: 'hips',
  torso: 'torso',
  chestPlateL: 'torso',
  chestPlateR: 'torso',
  backPlate: 'torso',
  pauldronL: 'torso',
  pauldronR: 'torso',
  pauldronLameL: 'torso',
  pauldronLameR: 'torso',
  eyeL: 'head',
  eyeR: 'head',
  ventL: 'torso',
  ventR: 'torso',
  head: 'head',
  jaw: 'head',
  tuskL: 'head',
  tuskR: 'head',
  'upper-arm-l': 'upperArmL',
  'fore-arm-l': 'foreArmL',
  handL: 'foreArmL',
  'upper-arm-r': 'upperArmR',
  'fore-arm-r': 'foreArmR',
  handR: 'handR',
  clubHandle: 'handR',
  clubHead: 'handR',
};

/** Boss stud parts are named `stud1`, `stud2`, … at build time — all bind to the club bone. */
export function bossPartBone(part: string): string {
  if (part.startsWith('stud')) return 'handR';
  const bone = BOSS_PART_BONE[part];
  if (!bone) throw new Error(`[rigs] boss part "${part}" has no bone binding`);
  return bone;
}

/** bone-name → index (rig order). Module-level, pure. */
export function boneIndex(rig: Rig): Map<string, number> {
  const map = new Map<string, number>();
  rig.bones.forEach((bone, i) => map.set(bone.name, i));
  return map;
}
